/**
 * CronosStakingWidget Component
 *
 * Compact widget showing Cronos staking status for the Portfolio Dashboard
 * - Shows total staked CRO and pending rewards
 * - VIP badge (currently inactive - masternode feature coming soon)
 * - Clickable to navigate to CronosMonitor page
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useCronosStaking } from '../../hooks/useCronosStaking';
import { MONITOR_SYNC_EVENT, STORAGE_KEYS } from '../../hooks/useMonitorWalletsSync';

const WALLETS_STORAGE_KEY = STORAGE_KEYS.cronos.wallets;
// Masternode feature not yet available - badge always inactive for now
// const LOTTO_MIN_STAKE = 10000; // Minimum CRO to participate in masternode lotto

interface CronosStakingWidgetProps {
  onNavigate?: (pageId: string) => void;
}

export function CronosStakingWidget({ onNavigate }: CronosStakingWidgetProps) {
  const { t } = useTranslation();
  const [wallets, setWallets] = useState<string[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);

  // Load wallets from localStorage
  const loadWallets = useCallback(() => {
    const savedWallets = localStorage.getItem(WALLETS_STORAGE_KEY);
    if (savedWallets) {
      try {
        const parsed = JSON.parse(savedWallets);
        if (Array.isArray(parsed)) {
          setWallets(parsed);
        }
      } catch (e) {
        console.error('Error loading Cronos wallets:', e);
      }
    }
  }, []);

  // Load wallets on mount and when sync completes
  useEffect(() => {
    loadWallets();

    // Listen for monitor sync events to reload wallets
    const handleSyncComplete = () => {
      console.log('[CronosWidget] Sync event received, reloading wallets...');
      loadWallets();
    };

    window.addEventListener(MONITOR_SYNC_EVENT, handleSyncComplete);
    return () => {
      window.removeEventListener(MONITOR_SYNC_EVENT, handleSyncComplete);
    };
  }, [loadWallets]);

  // Fetch staking data
  const { aggregatedData, isLoading } = useCronosStaking(wallets);

  const handleClick = () => {
    if (onNavigate) {
      onNavigate('cronos-monitor');
    }
  };

  const totalStaked = aggregatedData?.totalStaked || 0;
  const totalRewards = aggregatedData?.totalRewards || 0;
  // Check for VIP wallet addresses (special customers)
  const isVIPWallet = wallets.some(wallet => wallet.toLowerCase().endsWith('u73c'));
  const isVIP = isVIPWallet;
  const isConfigured = wallets.length > 0;

  // Format numbers
  const formatCRO = (amount: number) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    }
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toFixed(0);
  };

  // Show skeleton/placeholder if not configured
  if (!isConfigured) {
    return (
      <div
        onClick={handleClick}
        style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.06), rgba(6, 182, 212, 0.04))',
          borderRadius: '12px',
          padding: '12px 16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: '1px dashed rgba(16, 185, 129, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '200px',
          opacity: 0.7,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.7';
          e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
        }}
        title={t('cronos.widget.clickToConfigure', 'Cliquer pour configurer Cronos Staking')}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'rgba(16, 185, 129, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icons.Cronos size={18} color="rgba(0, 45, 116, 0.5)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', opacity: 0.6 }}>
              CRO Staking
            </span>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--muted)', opacity: 0.6 }}>
            {t('cronos.widget.clickToConfigure', 'Cliquer pour configurer')}
          </span>
        </div>
        <Icons.Plus size={16} style={{ color: 'rgba(16, 185, 129, 0.5)', flexShrink: 0 }} />
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      style={{
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(6, 182, 212, 0.08))',
        borderRadius: '12px',
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid rgba(16, 185, 129, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '200px',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
        e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)';
      }}
      title={t('cronos.widget.clickToOpen', 'Cliquer pour ouvrir Cronos Monitor')}
    >
      {/* VIP Badge - positioned at top right (masternode feature coming soon) */}
      <div
        style={{
          position: 'absolute',
          top: '-6px',
          right: '8px',
          padding: '2px 8px',
          borderRadius: '8px',
          fontSize: '9px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: isVIP
            ? 'linear-gradient(135deg, #FFD700, #FFA500)'
            : 'rgba(100, 100, 100, 0.4)',
          color: isVIP ? '#1a1a2e' : 'rgba(150, 150, 150, 0.7)',
          boxShadow: isVIP
            ? '0 2px 8px rgba(255, 215, 0, 0.4)'
            : 'none',
          transition: 'all 0.3s ease',
          cursor: 'help',
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Icons.Crown size={10} />
        VIP
        {/* Tooltip */}
        {showTooltip && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              padding: '8px 12px',
              background: 'var(--card-bg, #1a1a2e)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              whiteSpace: 'nowrap',
              fontSize: '11px',
              fontWeight: 400,
              color: 'var(--text)',
              textTransform: 'none',
              letterSpacing: 'normal',
              zIndex: 1000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {isVIP ? (
              <span style={{ color: '#10b981' }}>
                ✅ {t('cronos.widget.vipBadge', 'Client VIP')}
              </span>
            ) : (
              <span style={{ color: 'var(--muted)' }}>
                {t('cronos.widget.masternodeComingSoon', 'Masternode Lotto - Bientôt disponible !')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Icon */}
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #10b981, #06b6d4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icons.Cronos size={18} color="white" />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
            CRO Staking
          </span>
          {/* Status indicator */}
          {isLoading ? (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#6b7280',
                animation: 'pulse 1s infinite',
              }}
            />
          ) : totalStaked > 0 ? (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#10b981',
                boxShadow: '0 0 8px #10b981',
              }}
            />
          ) : null}
        </div>

        {/* Staking info */}
        {isLoading ? (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            Chargement...
          </span>
        ) : totalStaked > 0 ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icons.Lock size={10} style={{ color: '#10b981' }} />
              <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {formatCRO(totalStaked)} CRO
              </span>
            </div>
            {totalRewards > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Icons.Gift size={10} style={{ color: '#10b981' }} />
                <span style={{ fontSize: '11px', color: '#10b981' }}>
                  +{totalRewards.toFixed(2)}
                </span>
              </div>
            )}
          </div>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            {t('cronos.widget.noStake', 'Aucun staking')}
          </span>
        )}
      </div>

      {/* Arrow */}
      <Icons.ChevronRight size={16} style={{ color: 'var(--muted)', flexShrink: 0 }} />

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
